// ── Bandeja de mensajes V2 (diseño handoff, 2026-08-29) ──────────────────────
// La lista de la izquierda agrupa POR CONTACTO (como Outreach): un contacto
// con WhatsApp e Instagram es UNA conversación, no dos filas. La derecha es
// la BITÁCORA del contacto — entradas LogEntry colapsables con bandas por
// día, todos sus canales mezclados en orden cronológico — con el composer
// abajo (elegís por qué canal responder si hay más de uno).
// Checkboxes por fila + SelectionActionBar a page root (REGLA #18).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { Recorrido } from '../components/ui/Recorrido.js';
import { useRecorrido } from '../hooks/useRecorrido.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { ListItem } from '../components/ui/ListItem.js';
import { ListState } from '../components/ui/ListState.js';
import { ListToolbar } from '../components/ui/ListToolbar.js';
import { ListBandHeader } from '../components/ui/ListBandHeader.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { LogEntry, type LogChannel } from '../components/ui/LogEntry.js';
import { Button } from '../components/ui/Button.js';
import { IconButton } from '../components/ui/IconButton.js';
import { Textarea } from '../components/ui/Textarea.js';
import { Input } from '../components/ui/Input.js';
import { MessageComposer } from '../components/ui/MessageComposer.js';
import { Modal } from '../components/ui/Modal.js';
import { Pressable } from '../components/ui/Pressable.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { Icon, type IconName } from '../components/Icon.js';
import { useToast } from '../components/ui/Toast.js';
import { api, ApiError } from '../lib/api.js';
import {
  useDmInbox, useSendDmReply, useTakeOverThread, useReleaseThread,
  useProgramados, useCancelarProgramado,
  useSetThreadsRead, useSetThreadsHandled, useSetThreadsArchived,
  useApproveThreadProposal, useRejectThreadProposal,
  useSubirAdjunto, useDescartarAdjunto, type AdjuntoSubido, useResponderPorCorreo,
  useDocumentosDelHilo, type DocumentoEnlazable,
  type InboxThread, type InboxThreadDetail, type ThreadState, type InboxAttachment,
} from '../hooks/useDmInbox.js';
import { ApprovalCard } from '../components/ui/ApprovalCard.js';
import { VoiceNote } from '../components/ui/VoiceNote.js';
import { ChatAdjunto } from '../components/ui/ChatAdjunto.js';
import { useSavedReplies } from '../hooks/useSavedReplies.js';
import { useCreateContactsFromTriage } from '../hooks/useDmTriage.js';
import { ContactDetail } from '../components/contacts/ContactDetail.js';
import { FusionarContactos } from '../components/messages/FusionarContactos.js';
import { EnviarPlantilla } from '../components/messages/EnviarPlantilla.js';
import { ProgramarEnvio } from '../components/messages/ProgramarEnvio.js';

const ICONO_CANAL: Record<string, IconName> = {
  instagram: 'social-instagram',
  facebook: 'social-facebook',
  whatsapp: 'social-whatsapp',
  email: 'email',
  form: 'forms',
};

// LogEntry no tiene canal 'facebook' — Meta comparte familia visual con
// Instagram (mismo token --evo-ch-instagram); el icono sí es el de Facebook.
const LOG_CHANNEL: Record<string, LogChannel> = {
  instagram: 'instagram',
  whatsapp: 'whatsapp',
  facebook: 'instagram',
  email: 'email',
  form: 'system',
};

const ALL_BANDS = new Set(['today', 'yesterday', 'this_week', 'this_month', 'older_2_3_months', 'older']);

/**
 * Texto a mostrar cuando el mensaje no tiene cuerpo (2026-08-19).
 *
 * 29 de 176 mensajes reales de Instagram llegaron vacíos: son fotos, reels
 * compartidos y reacciones. Verificado contra la API — INSTAGRAM_LIST_ALL_MESSAGES
 * devuelve solo created_time, from, id, message y to. No hay campo de adjunto ni
 * de tipo, y ninguna otra herramienta del toolkit lo expone, así que NO se puede
 * saber qué es.
 *
 * El placeholder vive acá y no en la base: el mensaje de verdad está vacío, y
 * escribir un texto inventado en body haría que la bitácora del contacto y una
 * eventual exportación mostraran algo que el remitente nunca escribió. Si algún
 * día hay soporte de adjuntos, esto se borra y no hay datos que limpiar.
 */
function esSinTexto(body: string | null | undefined): boolean {
  return !body || !body.trim();
}

function nombreDe(t: InboxThread): string {
  // El contacto vinculado primero: un wa_id o un id de Instagram no le dice
  // nada a nadie.
  return t.contactName ?? t.senderDisplayName ?? t.identifier;
}

function CanalIcono({ platform }: { platform: string }) {
  const icono = ICONO_CANAL[platform];
  if (!icono) return null;
  return (
    <span style={{ display: 'inline-flex', color: 'var(--evo-fg-3)', flexShrink: 0 }}>
      <Icon name={icono} size={14} />
    </span>
  );
}

// ── Agrupación por contacto ──────────────────────────────────────────────────

interface ConvGroup {
  key: string;
  name: string;
  contactId: number | null;
  threads: InboxThread[];
  lastAt: string | null;
  lastPreview: string | null;
  state: ThreadState;
  hoursLeft: number | null;
  takenOver: boolean;
  /** Atendida: leída en TODOS sus canales. Si uno quedó sin mirar, no lo está. */
  atendida: boolean;
  platforms: string[];
}

const STATE_RANK: Record<ThreadState, number> = { expiring: 2, unanswered: 1, answered: 0 };

function agrupar(threads: InboxThread[]): ConvGroup[] {
  const map = new Map<string, ConvGroup>();
  for (const th of threads) {
    const key = th.contactId != null ? `c${th.contactId}` : `t${th.id}`;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, {
        key, name: nombreDe(th), contactId: th.contactId,
        threads: [th], lastAt: th.lastMessageAt, lastPreview: th.lastMessagePreview,
        state: th.state, hoursLeft: th.hoursLeft,
        takenOver: th.takenOverAt != null, atendida: th.isHandled, platforms: [th.platform],
      });
      continue;
    }
    prev.threads.push(th);
    if (!prev.platforms.includes(th.platform)) prev.platforms.push(th.platform);
    prev.takenOver = prev.takenOver || th.takenOverAt != null;
    prev.atendida = prev.atendida && th.isHandled;
    if (STATE_RANK[th.state] > STATE_RANK[prev.state]) {
      prev.state = th.state;
      prev.hoursLeft = th.hoursLeft;
    }
    if ((th.lastMessageAt ?? '') > (prev.lastAt ?? '')) {
      prev.lastAt = th.lastMessageAt;
      prev.lastPreview = th.lastMessagePreview;
    }
  }
  return [...map.values()].sort((a, b) => (b.lastAt ?? '').localeCompare(a.lastAt ?? ''));
}

/**
 * Lo que la fila avisa.
 *
 * "Sin contestar" mira quién mandó el último mensaje, y eso deja marcada a toda
 * conversación que el cliente cierra con un "gracias". Marcarla leída es decir
 * "esto ya está": desde entonces la fila se calla, aunque nadie haya escrito
 * una respuesta — porque no hacía falta.
 *
 * El dato no se toca: en la base la conversación sigue sin responder y el
 * filtro "Sin responder" la sigue mostrando. Lo que cambia es lo que la
 * pantalla te reclama.
 */
function EstadoPills({ g }: { g: ConvGroup }) {
  const { t } = useTranslation();
  const pills = [];
  if (g.takenOver) {
    pills.push(<Pill key="tomada" size="sm" variant="subtle" tone="amber">{t('messages.takenOver')}</Pill>);
  }
  if (g.atendida) return <>{pills}</>;
  if (g.state === 'expiring') {
    pills.push(
      <Pill key="vence" size="sm" variant="subtle" tone="amber">
        {t('messages.expiringIn', { hours: Math.max(Math.ceil(g.hoursLeft ?? 0), 1) })}
      </Pill>,
    );
  } else if (g.state === 'unanswered') {
    pills.push(<Pill key="sr" size="sm" variant="subtle" tone="cyan">{t('messages.unanswered')}</Pill>);
  }
  return <>{pills}</>;
}

// ── Bitácora del contacto (panel derecho) ────────────────────────────────────

interface LogItem {
  key: string;
  threadId: number;
  platform: string;
  direction: 'inbound' | 'outbound';
  body: string;
  origin: string | null;
  sendError: string | null;
  at: string;
  attachments: InboxAttachment[];
  /** Su id de WhatsApp — con esto se lo puede citar. Null en un correo o en un
   *  borrador que todavía no salió. */
  platformMessageId: string | null;
  /** El mensaje que este cita, ya resuelto por el servidor. */
  replyTo: { id: number; direction: 'inbound' | 'outbound'; body: string } | null;
}

/** Un texto largo no puede empujar la burbuja: la cita es una referencia, no
 *  el mensaje. WhatsApp hace lo mismo. */
function recortar(texto: string, max = 120): string {
  const limpio = texto.replace(/\s+/g, ' ').trim();
  return limpio.length > max ? `${limpio.slice(0, max)}…` : limpio;
}

/**
 * El mensaje citado, arriba del que responde.
 *
 * La barra de color a la izquierda y el texto atenuado son la convención de
 * todos los chats, y por eso no lleva la palabra "respondiendo a": quien lo ve
 * ya sabe qué significa.
 */
function CitaDelMensaje({ cita, onIr }: {
  cita: { id: number; direction: 'inbound' | 'outbound'; body: string };
  onIr?: () => void;
}) {
  const { t } = useTranslation();
  const vacio = esSinTexto(cita.body);
  return (
    <Pressable onPress={() => onIr?.()} ariaLabel={t('messages.goToQuoted', 'Ir al mensaje citado')}>
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)',
        borderLeft: '2px solid var(--evo-cyan)',
        paddingLeft: 'var(--evo-space-3)',
        textAlign: 'left', width: '100%',
      }}>
        <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-cyan)' }}>
          {cita.direction === 'outbound' ? t('messages.quotedYou', 'Tú') : t('messages.quotedThem', 'El cliente')}
        </span>
        <span style={{
          fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)',
          fontStyle: vacio ? 'italic' : undefined,
        }}>
          {vacio ? t('messages.nonTextContent') : recortar(cita.body)}
        </span>
      </div>
    </Pressable>
  );
}

function bandaDelDia(iso: string, hoy: string, ayer: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return hoy;
  const y = new Date(now); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return ayer;
  return d.toLocaleDateString('es', { day: 'numeric', month: 'short', year: '2-digit' });
}

/**
 * Un mensaje de la conversación, dentro del acordeón de su canal.
 *
 * Burbujas enfrentadas, como en cualquier chat: lo que te escribieron a la
 * izquierda, lo que contestaste a la derecha. Antes iba todo alineado a un solo
 * lado como un registro, y para saber quién había dicho qué había que leer la
 * etiqueta de cada línea en vez de verlo de un vistazo.
 */
/**
 * Un correo dentro de su acordeón.
 *
 * No va en burbuja: un correo no es un chat. Se lee como en Leads —quién
 * escribió, cuándo, y el texto completo debajo— porque un correo se lee entero,
 * no se escanea de un vistazo como un mensaje de WhatsApp.
 */
function CorreoDelHilo({ item }: { item: LogItem }) {
  const { t } = useTranslation();
  const entrante = item.direction === 'inbound';
  // El asunto es la primera línea: así lo arma el servidor.
  const [asunto, ...resto] = item.body.split('\n\n');
  const cuerpo = resto.join('\n\n').trim();

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)',
      padding: 'var(--evo-space-4)',
      borderRadius: 'var(--evo-r)',
      background: 'var(--evo-panel-strong)',
      borderLeft: `2px solid ${entrante ? 'var(--evo-cyan)' : 'var(--evo-stroke)'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--evo-space-3)' }}>
        <span style={{
          fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)',
          textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0,
        }}>
          {entrante ? t('messages.fromThem') : t('messages.fromUs')}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', flexShrink: 0 }}>
          {new Date(item.at).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      {asunto && (
        <span style={{ fontSize: 'var(--evo-fs-body)', fontWeight: 'var(--evo-fw-medium)', color: 'var(--evo-fg-1)' }}>
          {asunto}
        </span>
      )}
      {cuerpo && (
        <div style={{
          fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)',
          lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {cuerpo}
        </div>
      )}
    </div>
  );
}

/** Citar este mensaje. Siempre montado; el hover solo lo hace más evidente. */
function BotonDeCita({ item, onCitar, visible }: {
  item: LogItem;
  onCitar: (item: LogItem) => void;
  visible: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Pressable onPress={() => onCitar(item)} ariaLabel={t('messages.replyTo')}>
      <span style={{
        color: 'var(--evo-fg-4)', display: 'inline-flex',
        // Tenue en reposo y pleno al pasar por encima. En táctil se queda en
        // el valor de reposo, que es suficiente para verlo y tocarlo.
        //
        // Sin transición: no hay token de duración corta en tokens.css y
        // hardcodear los milisegundos está prohibido (REGLA #2). El cambio
        // instantáneo se ve bien; si algún día existe el token, se suma acá.
        opacity: visible ? 1 : 0.35,
      }}>
        <Icon name="reply" size={14} />
      </span>
    </Pressable>
  );
}

function MensajeDelHilo({ item, onCitar, citable }: {
  item: LogItem;
  /** Empezar a responder citando ESTE mensaje. Ausente = no se ofrece. */
  onCitar?: (item: LogItem) => void;
  /** Si este canal admite citas. Hoy solo WhatsApp. */
  citable?: boolean;
}) {
  const { t } = useTranslation();
  const entrante = item.direction === 'inbound';
  const audios = item.attachments.filter(a => a.kind === 'audio');
  const otros = item.attachments.filter(a => a.kind !== 'audio');
  const [encima, setEncima] = useState(false);

  // Sin id de plataforma no hay a qué apuntar: un borrador sin enviar, o un
  // mensaje anterior a que se guardaran los ids.
  const puedeCitarse = Boolean(citable && onCitar && item.platformMessageId);

  return (
    <div
      style={{
        display: 'flex', justifyContent: entrante ? 'flex-start' : 'flex-end',
        alignItems: 'center', gap: 'var(--evo-space-2)',
      }}
      onMouseEnter={() => setEncima(true)}
      onMouseLeave={() => setEncima(false)}
    >
      {/* El botón va del lado de AFUERA de la burbuja: adentro competiría con
          el texto, y del lado de adentro taparía el contenido en los mensajes
          cortos.

          Está SIEMPRE montado y solo cambia de opacidad, en vez de aparecer y
          desaparecer con el hover. En una pantalla táctil no hay hover: un
          botón condicionado a pasar el mouse por encima no existe en un
          teléfono, y esta bandeja se contesta desde el teléfono tanto como
          desde la computadora. */}
      {puedeCitarse && !entrante && (
        <BotonDeCita item={item} onCitar={onCitar!} visible={encima} />
      )}

      <div style={{
        maxWidth: 'var(--evo-chat-bubble-max-w)', minWidth: 0,
        display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)',
        padding: 'var(--evo-chat-bubble-py) var(--evo-chat-bubble-px)',
        borderRadius: 'var(--evo-chat-bubble-r)',
        background: entrante ? 'var(--evo-chat-bubble-in-bg)' : 'var(--evo-chat-bubble-out-bg)',
      }}>
        {/* La cita va ARRIBA del texto: es el contexto de lo que sigue, y
            leerla después sería leer la respuesta sin saber a qué. */}
        {item.replyTo && <CitaDelMensaje cita={item.replyTo} />}

        {!esSinTexto(item.body) && (
          <div style={{
            fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)',
            lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {item.body}
          </div>
        )}

        {esSinTexto(item.body) && item.attachments.length === 0 && (
          <span style={{ fontStyle: 'italic', fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>
            {t('messages.nonTextContent')}
          </span>
        )}

        {audios.map(a => <VoiceNote key={a.id} url={a.url} error={a.error} />)}
        {otros.map(a => (
          <ChatAdjunto
            key={a.id} kind={a.kind} url={a.url} mimeType={a.mimeType}
            filename={a.filename} sizeBytes={a.sizeBytes} error={a.error}
          />
        ))}

        {/* Un envío fallido tiene que verse en el hilo, no solo en un toast que
            ya se fue. */}
        {item.sendError && (
          <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-danger)' }}>
            {item.sendError}
          </span>
        )}

        <span style={{
          alignSelf: 'flex-end', fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {new Date(item.at).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      {/* El del cliente va después de su burbuja, por el mismo motivo que el
          otro va antes: siempre del lado de afuera. */}
      {puedeCitarse && entrante && (
        <BotonDeCita item={item} onCitar={onCitar!} visible={encima} />
      )}
    </div>
  );
}

function Bitacora({ group, verFicha, onVerFicha }: {
  group: ConvGroup;
  verFicha: boolean;
  onVerFicha: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const send = useSendDmReply();
  const takeover = useTakeOverThread();
  const release = useReleaseThread();
  const aprobar = useApproveThreadProposal();
  const rechazar = useRejectThreadProposal();
  const guardarContacto = useCreateContactsFromTriage();
  const setHandled = useSetThreadsHandled();
  /** Atendida cuando lo están TODOS sus canales: si a uno le falta respuesta,
   *  la conversación no está cerrada. */
  const estaAtendida = group.threads.every(th => th.isHandled);
  const responderCorreo = useResponderPorCorreo();
  const subirAdjunto = useSubirAdjunto();
  const descartarAdjunto = useDescartarAdjunto();
  /** Archivos ya subidos, esperando que se mande el mensaje. */
  const [adjuntos, setAdjuntos] = useState<AdjuntoSubido[]>([]);
  const [programando, setProgramando] = useState(false);
  const [eligiendoDoc, setEligiendoDoc] = useState(false);
  const inputArchivo = useRef<HTMLInputElement | null>(null);
  const [texto, setTexto] = useState('');
  /** El mensaje que se está citando, si hay uno. Vive acá y no en la burbuja
   *  porque lo consume el compositor, que está del otro lado de la pantalla. */
  const [citando, setCitando] = useState<LogItem | null>(null);
  const [eligiendo, setEligiendo] = useState(false);
  const [enviandoPlantilla, setEnviandoPlantilla] = useState(false);
  const [buscarRespuesta, setBuscarRespuesta] = useState('');
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  /** El área que scrollea. Se la lleva al final sola: una conversación se abre
   *  por lo último, no por lo primero. */
  const scroller = useRef<HTMLDivElement | null>(null);
  const [canalActivo, setCanalActivo] = useState<number | null>(null);

  // Un fetch por thread del contacto — misma queryKey que useDmThread, así
  // React Query comparte caché con cualquier otra vista del hilo.
  const detalles = useQueries({
    queries: group.threads.map(th => ({
      queryKey: ['dm-thread', th.id],
      queryFn: () => api.get<InboxThreadDetail>(`/api/dm/threads/${th.id}`),
      refetchInterval: 10_000,
      refetchOnWindowFocus: true,
    })),
  });
  const cargando = detalles.some(q => q.isLoading);
  const detallesOk = detalles.map(q => q.data).filter((d): d is InboxThreadDetail => d != null);

  const entradas: LogItem[] = useMemo(() => {
    const out: LogItem[] = [];
    for (const d of detallesOk) {
      for (const m of d.messages) {
        out.push({
          key: `m${d.id}-${m.id}`, threadId: d.id, platform: d.platform,
          direction: m.direction, body: m.body, origin: m.origin,
          sendError: m.sendError, at: m.at, attachments: m.attachments ?? [],
          platformMessageId: m.platformMessageId ?? null,
          replyTo: m.replyTo ?? null,
        });
      }
    }
    return out.sort((a, b) => a.at.localeCompare(b.at));
  }, [detallesOk]);

  const propuestas = useMemo(
    () => detallesOk.flatMap(d => d.proposals.map(p => ({ ...p, threadId: d.id }))),
    [detallesOk],
  );

  // Canal de respuesta: el del último mensaje ENTRANTE (ahí está la
  // conversación viva); si el usuario eligió otro chip, manda el chip.
  // A qué canal le responde el compositor: al que estés mirando, siempre que se
  // pueda escribir por ahí. Un envío de formulario se lee y no se contesta —
  // nadie responde un formulario— así que no entra en la elección.
  const respondibles = group.threads.filter(th => th.canReply);
  const ultimoInbound = [...entradas].reverse().find(e => e.direction === 'inbound');
  const threadRespuesta = respondibles.find(th => th.id === (canalActivo ?? ultimoInbound?.threadId))
    ?? respondibles[0] ?? group.threads[0]!;
  const esCorreo = threadRespuesta.platform === 'email';
  const detalleRespuesta = detallesOk.find(d => d.id === threadRespuesta.id);
  const programados = useProgramados(threadRespuesta.id);
  const cancelarProgramado = useCancelarProgramado(threadRespuesta.id);

  // Se pide recién cuando se abre el selector: son tres consultas que nadie
  // necesita hasta que va a mandar algo.
  const documentos = useDocumentosDelHilo(threadRespuesta.id, eligiendoDoc);
  const ventanaCerrada = detalleRespuesta != null && detalleRespuesta.hoursLeft != null && detalleRespuesta.hoursLeft <= 0;
  const sinContacto = group.contactId == null;
  const tomada = group.threads.some(th => th.takenOverAt != null);
  const { data: respuestas } = useSavedReplies(!ventanaCerrada);

  async function elegirArchivos(lista: FileList | null) {
    if (!lista || lista.length === 0) return;
    for (const file of Array.from(lista)) {
      try {
        const subido = await subirAdjunto.mutateAsync(file);
        setAdjuntos(prev => [...prev, subido]);
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : String(err));
      }
    }
    // Se limpia el input: sin esto, volver a elegir el MISMO archivo no dispara
    // el evento y parece que no pasó nada.
    if (inputArchivo.current) inputArchivo.current.value = '';
  }

  /** El enlace se agrega al final del mensaje, en su propio renglón: pegado al
   *  texto, WhatsApp a veces se come el último carácter dentro del enlace. */
  function insertarDocumento(d: DocumentoEnlazable) {
    setTexto(prev => (prev.trim() ? `${prev.trimEnd()}\n\n${d.url}` : d.url));
    setEligiendoDoc(false);
  }

  function quitarAdjunto(id: number) {
    setAdjuntos(prev => prev.filter(a => a.id !== id));
    descartarAdjunto.mutate(id);
  }

  /** Inserta al final en vez de reemplazar: si ya escribiste algo, un clic no
   *  puede borrártelo. Se separa con un renglón en blanco cuando había texto. */
  function insertarRespuesta(body: string) {
    setTexto(prev => (prev.trim() ? `${prev.trimEnd()}\n\n${body}` : body));
    setEligiendo(false);
    // La próxima apertura arranca limpia: abrir con lo que se buscó la vez
    // pasada esconde respuestas que sí están.
    setBuscarRespuesta('');
  }

  const respuestasFiltradas = useMemo(() => {
    const todas = respuestas?.replies ?? [];
    const q = buscarRespuesta.trim().toLowerCase();
    if (!q) return todas;
    return todas.filter(r =>
      r.title.toLowerCase().includes(q) || r.body.toLowerCase().includes(q),
    );
  }, [respuestas?.replies, buscarRespuesta]);

  async function responderCorreoAhora() {
    if (!texto.trim()) return;
    try {
      await responderCorreo.mutateAsync({ threadId: threadRespuesta.id, body: texto.trim() });
      setTexto('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function responder() {
    // Con un archivo no hace falta texto: mandar una factura sin escribir nada
    // es un caso normal.
    if (!texto.trim() && adjuntos.length === 0) return;
    try {
      await send.mutateAsync({
        threadId: threadRespuesta.id, body: texto.trim(),
        attachmentIds: adjuntos.map(a => a.id),
        replyToPlatformId: citando?.platformMessageId ?? null,
      });
      setTexto('');
      setAdjuntos([]);
      setCitando(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  /** Tomar/devolver aplica a TODOS los hilos del contacto: la conversación
   *  es del contacto, no de un canal — tomar solo WhatsApp dejaría al
   *  asistente contestando por Instagram a la misma persona. */
  function toggleTomada() {
    const m = tomada ? release : takeover;
    for (const th of group.threads) {
      if (tomada ? th.takenOverAt != null : th.takenOverAt == null) {
        m.mutate(th.id, {
          onError: err => toast.error(err instanceof ApiError ? err.message : String(err)),
        });
      }
    }
  }

  function toggleEntrada(key: string) {
    setAbiertas(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  // Un acordeón por CANAL, no por mensaje.
  //
  // La conversación de una persona en WhatsApp es UNA cosa. Partirla en un
  // acordeón por línea obligaba a abrir cinco cajas para leer lo que en el
  // teléfono se lee de un vistazo, y escondía justamente lo que importa: el
  // ida y vuelta.
  const canales = useMemo(() => {
    const porCanal = new Map<string, LogItem[]>();
    for (const e of entradas) {
      const lista = porCanal.get(e.platform) ?? [];
      lista.push(e);
      porCanal.set(e.platform, lista);
    }
    return [...porCanal.entries()]
      .map(([platform, items]) => ({
        platform,
        items,
        ultimo: items[items.length - 1]?.at ?? '',
        // Bandas por día DENTRO del canal: un canal no tiene una fecha, sus
        // mensajes sí.
        bandas: items.reduce<Array<{ label: string; items: LogItem[] }>>((acc, e) => {
          const label = bandaDelDia(e.at, t('common.today'), t('common.yesterday'));
          const last = acc[acc.length - 1];
          if (last && last.label === label) last.items.push(e);
          else acc.push({ label, items: [e] });
          return acc;
        }, []),
      }))
      .sort((a, b) => b.ultimo.localeCompare(a.ultimo));
  }, [entradas, t]);

  // Al abrir una conversación, el canal donde escribió último ya está abierto:
  // tener que hacer un clic para ver lo que se acaba de recibir es un clic de
  // más en lo único que se vino a hacer.
  useEffect(() => {
    const masReciente = canales[0]?.platform;
    setAbiertas(masReciente ? new Set([masReciente]) : new Set());
  }, [group.key, canales.length]);

  // Abajo del todo: al abrir la conversación y cada vez que entra o sale un
  // mensaje. Sin esto lo último queda debajo del corte y parece que no llegó
  // —pasó de verdad: un mensaje enviado estaba guardado, contado en la
  // cabecera, y fuera de la vista.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [group.key, entradas.length, abiertas]);

  const totalMensajes = entradas.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, minHeight: 0 }}>
      {/* Cabecera del diseño: canal · nombre · pill · "N mensajes · M canales"
          · expandir/colapsar · tomar/devolver. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)',
        padding: 'var(--evo-space-3) var(--evo-space-4)',
        borderBottom: '1px solid var(--evo-stroke)', minHeight: 'var(--evo-toolbar-height)',
        boxSizing: 'border-box', flexShrink: 0,
      }}>
        <CanalIcono platform={threadRespuesta.platform} />
        <span style={{
          fontSize: 'var(--evo-fs-body)', fontWeight: 'var(--evo-fw-medium)',
          color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {group.name}
        </span>
        {tomada && <Pill size="sm" variant="subtle" tone="amber">{t('messages.takenOver')}</Pill>}
        <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
          {t('messages.headerMeta', { msgs: totalMensajes, channels: group.platforms.length })}
        </span>
        <span style={{ flex: 1 }} />
        <Button variant="ghost" tone="cyan" size="sm" onClick={() => setAbiertas(new Set(canales.map(c => c.platform)))}>
          {t('common.expandAll')}
        </Button>
        <Button variant="ghost" tone="cyan" size="sm" onClick={() => setAbiertas(new Set())}>
          {t('common.collapseAll')}
        </Button>
        {/* Abrir una conversación la marca LEÍDA, que es otra cosa. Esto la da
            por cerrada: el cliente escribió un "gracias" y no hay nada que
            contestar, así que deja de reclamar sin obligar a escribir una
            respuesta de compromiso. Si vuelven a escribir, se reabre sola. */}
        {/* La ficha del contacto al costado: para ver el teléfono, el correo o
            una nota sin salir de la conversación. */}
        {group.contactId != null && (
          <IconButton
            icon={<Icon name="contacts" size={14} />}
            ariaLabel={t('messages.toggleContact')}
            size="sm"
            variant={verFicha ? 'solid' : 'outline'}
            tone={verFicha ? 'cyan' : 'muted'}
            onClick={onVerFicha}
          />
        )}
        <Button
          variant="outline"
          tone="muted"
          size="sm"
          loading={setHandled.isPending}
          onClick={() => setHandled.mutate({ ids: group.threads.map(th => th.id), handled: !estaAtendida })}
        >
          {estaAtendida ? t('messages.markPending') : t('messages.markHandled')}
        </Button>
        <Button
          variant="outline"
          tone={tomada ? 'cyan' : 'muted'}
          size="sm"
          loading={takeover.isPending || release.isPending}
          onClick={toggleTomada}
        >
          {tomada ? t('messages.releaseControl') : t('messages.takeControl')}
        </Button>
      </div>

      <ListState
        isLoading={cargando && entradas.length === 0}
        isError={detalles.some(q => q.isError)}
        error={detalles.find(q => q.error)?.error ?? null}
        isEmpty={!cargando && entradas.length === 0}
        emptyTitle={t('messages.emptyThread')}
      >
        <div ref={scroller} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 'var(--evo-space-4) 0', display: 'flex', flexDirection: 'column', gap: 'var(--evo-log-gap)' }}>
          {canales.map(canal => (
            // El acordeón no se comprime.
            //
            // `LogEntry` lleva `overflow: hidden` para redondear sus esquinas, y
            // como ítem de un flex se encoge por defecto: adentro de un área que
            // scrollea eso significa que la conversación se recorta contra el
            // borde en silencio, sin barra para bajar, mientras el contenedor de
            // afuera cree que entra todo. Medido en producción: 1196px de
            // conversación dentro de 584px de caja.
            <div key={canal.platform} style={{ flexShrink: 0 }}>
            <LogEntry
              channel={LOG_CHANNEL[canal.platform] ?? 'sms'}
              channelLabel={t(`messages.channel.${canal.platform}`, { defaultValue: canal.platform })}
              {...(ICONO_CANAL[canal.platform] ? { icon: ICONO_CANAL[canal.platform]! } : {})}
              direction={canal.items[canal.items.length - 1]?.direction === 'inbound' ? 'in' : 'out'}
              title={t('messages.channelSummary', { count: canal.items.length })}
              time={new Date(canal.ultimo).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              open={abiertas.has(canal.platform)}
              onToggle={() => {
                toggleEntrada(canal.platform);
                // Abrir un canal es elegirlo para responder. Antes el compositor
                // se quedaba en el último mensaje entrante sin importar qué
                // estuvieras mirando: abrías el correo y lo que escribías salía
                // por WhatsApp.
                const suyo = canal.items[canal.items.length - 1]?.threadId;
                if (suyo != null) setCanalActivo(suyo);
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
                {canal.bandas.map(banda => (
                  <div key={`${canal.platform}-${banda.label}`} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-chat-bubble-gap)' }}>
                    <ListBandHeader plain label={banda.label} count={banda.items.length} />
                    {banda.items.map(e => (
                      e.platform === 'email'
                        ? <CorreoDelHilo key={e.key} item={e} />
                        : (
                          <MensajeDelHilo
                            key={e.key} item={e}
                            citable={e.platform === 'whatsapp'}
                            onCitar={setCitando}
                          />
                        )
                    ))}
                  </div>
                ))}
              </div>
            </LogEntry>
            </div>
          ))}
        </div>
      </ListState>

      {/* Propuesta de Evo Brain. Va ACÁ, entre la conversación y el
          compositor: para decidir si una respuesta corresponde hay que haber
          leído lo que preguntó la persona. Sigue estando también en la cola. */}
      {propuestas.length > 0 && (
        <div style={{ padding: '0 var(--evo-space-4) var(--evo-space-3)' }}>
          <ApprovalCard
            proposals={propuestas.map(p => ({
              id: p.id,
              taskId: p.taskId,
              turnGroupId: p.turnGroupId,
              toolName: p.toolName,
              verb: p.verb as 'send',
              sentence: p.sentence,
              irreversible: p.irreversible,
              ...(p.warning ? { warning: p.warning } : {}),
              createdAt: p.createdAt,
              status: p.status as 'pending',
            }))}
            onApprove={taskId => {
              const p = propuestas.find(x => x.taskId === taskId);
              aprobar.mutate({ taskId, threadId: p?.threadId ?? threadRespuesta.id },
                { onError: err => toast.error(err instanceof ApiError ? err.message : String(err)) });
            }}
            onReject={taskId => {
              const p = propuestas.find(x => x.taskId === taskId);
              rechazar.mutate({ taskId, threadId: p?.threadId ?? threadRespuesta.id },
                { onError: err => toast.error(err instanceof ApiError ? err.message : String(err)) });
            }}
            approvingId={aprobar.isPending ? aprobar.variables?.taskId ?? null : null}
            rejectingId={rechazar.isPending ? rechazar.variables?.taskId ?? null : null}
          />
        </div>
      )}

      {/* Compositor. Fuera de la ventana NO se ofrece escribir: en WhatsApp
          haría falta una plantilla aprobada, y en el resto directamente no
          llega. Decirlo antes evita escribir un mensaje que se va a perder. */}
      <div style={{ borderTop: '1px solid var(--evo-stroke)', padding: 'var(--evo-space-4)', flexShrink: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
            {/* Todavía no es nadie en la libreta: se puede contestar igual, y
                anotarlo cuando la conversación demuestre que vale la pena. */}
            {sinContacto && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)' }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                  {t('messages.notAContactYet')}
                </span>
                <Button
                  variant="outline" tone="cyan" size="sm"
                  loading={guardarContacto.isPending}
                  onClick={() => guardarContacto.mutate(group.threads.map(th => th.channelId))}
                >
                  {t('messages.saveAsContact')}
                </Button>
              </div>
            )}
            {/* Más de un canal: chips para elegir por dónde sale la respuesta. */}
            {respondibles.length > 1 && (
              <PillGroup size="sm">
                {respondibles.map(th => (
                  <Pill
                    key={th.id}
                    tone={th.id === threadRespuesta.id ? 'cyan' : 'neutral'}
                    onClick={() => setCanalActivo(th.id)}
                  >
                    {t(`messages.channel.${th.platform}`, { defaultValue: th.platform })}
                  </Pill>
                ))}
              </PillGroup>
            )}
            {esCorreo ? (
              /* El compositor de correo, el mismo primitive que usa Leads: un
                 correo se escribe distinto que un mensaje de WhatsApp. El
                 destinatario, la casilla y el asunto los arma el servidor a
                 partir del último correo recibido. */
              <MessageComposer
                value={texto}
                onChange={setTexto}
                placeholder={t('messages.replyPlaceholder')}
                primaryLabel={t('messages.sendBtn')}
                loadingLabel={t('messages.sending', { defaultValue: '' })}
                cancelLabel={t('common.cancel')}
                loading={responderCorreo.isPending}
                onPrimary={() => { void responderCorreoAhora(); }}
                onCancel={() => setTexto('')}
              />
            ) : ventanaCerrada ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-2)' }}>
                  <span style={{ color: 'var(--evo-warning)', display: 'inline-flex', flexShrink: 0, marginTop: '1px' }}>
                    <Icon name="alert" size={14} />
                  </span>
                  <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', lineHeight: 1.5 }}>
                    {threadRespuesta.platform === 'whatsapp' ? t('messages.windowClosedWhatsapp') : t('messages.windowClosed')}
                  </span>
                </div>

                {/* La salida, donde antes solo estaba el problema. En WhatsApp
                    fuera de ventana SÍ se puede escribir —con una plantilla
                    aprobada— y decir que no se puede sin ofrecer eso deja a
                    alguien creyendo que perdió el contacto. */}
                {threadRespuesta.platform === 'whatsapp' && (
                  <div>
                    <Button
                      variant="outline" tone="cyan" size="sm"
                      onClick={() => setEnviandoPlantilla(true)}
                    >
                      {t('sendTemplate.open')}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Lo que ya se subió y todavía no se mandó. Se ve antes de
                    mandarlo: una factura se mira antes de pasársela a alguien. */}
                {adjuntos.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
                    {adjuntos.map(a => (
                      <span key={a.id} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)',
                        padding: 'var(--evo-space-1) var(--evo-space-3)',
                        background: 'var(--evo-bg-2)', borderRadius: 'var(--evo-r-pill)',
                        fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-2)', maxWidth: '100%',
                      }}>
                        <Icon name="attach" size={12} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.filename ?? t('messages.attachmentFile')}
                        </span>
                        <IconButton
                          icon={<Icon name="close" size={12} />}
                          ariaLabel={t('messages.attachmentRemove')}
                          size="sm" variant="ghost"
                          onClick={() => quitarAdjunto(a.id)}
                        />
                      </span>
                    ))}
                  </div>
                )}

                {/* A qué se está respondiendo, arriba del campo. Sin esto se
                    elige citar un mensaje, se escribe, y no queda ninguna
                    señal de que la respuesta va a salir citando algo —ni de
                    cuál, si uno se distrajo entre medio. */}
                {citando && (
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-2)',
                    padding: 'var(--evo-space-2) var(--evo-space-3)',
                    background: 'var(--evo-bg-2)', borderRadius: 'var(--evo-r)',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CitaDelMensaje cita={{
                        id: 0,
                        direction: citando.direction,
                        body: citando.body,
                      }} />
                    </div>
                    <IconButton
                      icon={<Icon name="close" size={12} />}
                      ariaLabel={t('messages.cancelQuote', 'No citar este mensaje')}
                      size="sm" variant="ghost"
                      onClick={() => setCitando(null)}
                    />
                  </div>
                )}

                {/* Lo que va a salir solo, y a qué hora. Va acá arriba y no
                    escondido en un menú: un mensaje programado que nadie
                    recuerda haber programado es peor que no tener la función.
                    Se puede cancelar mientras no haya salido. */}
                {(programados.data?.scheduled.length ?? 0) > 0 && (
                  <div style={{
                    display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)',
                    marginBottom: 'var(--evo-space-3)',
                  }}>
                    {programados.data!.scheduled.map(p => (
                      <div key={p.id} style={{
                        display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)',
                        padding: 'var(--evo-space-2) var(--evo-space-3)',
                        borderRadius: 'var(--evo-r)',
                        border: '1px solid var(--evo-stroke)',
                      }}>
                        <Icon name="clock" size={14} />
                        <span style={{
                          flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-caption)',
                          color: 'var(--evo-fg-2)', overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {t('messages.schedule.pending', 'Sale {{cuando}}', {
                            cuando: new Date(p.scheduledFor).toLocaleString(undefined, {
                              dateStyle: 'short', timeStyle: 'short',
                            }),
                          })}
                          {p.body ? ` · ${p.body}` : ''}
                        </span>
                        <IconButton
                          icon={<Icon name="close" size={12} />}
                          ariaLabel={t('messages.schedule.cancel', 'Cancelar el envío programado')}
                          size="sm" variant="ghost"
                          loading={cancelarProgramado.isPending}
                          onClick={() => cancelarProgramado.mutate(p.id, {
                            onError: err => toast.error(err instanceof ApiError ? err.message : String(err)),
                          })}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Enter manda; Shift+Enter hace el salto de línea. Es como se
                    escribe en cualquier chat, y tener que bajar al botón para
                    cada mensaje corto es lo que hace sentir lenta una bandeja. */}
                <Textarea
                  value={texto} onChange={setTexto} rows={3}
                  placeholder={t('messages.replyPlaceholder')} fullWidth
                  onEnterSubmit={() => { void responder(); }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)' }}>
                  {/* El input real va escondido: el que se ve es el botón del
                      sistema, no el control gris del navegador. */}
                  <input
                    ref={inputArchivo}
                    type="file"
                    multiple
                    onChange={e => { void elegirArchivos(e.target.files); }}
                    style={{ display: 'none' }}
                  />
                  <IconButton
                    icon={<Icon name="attach" size={14} />}
                    ariaLabel={t('messages.attachFile')}
                    size="sm"
                    variant="outline"
                    loading={subirAdjunto.isPending}
                    onClick={() => inputArchivo.current?.click()}
                  />
                  {/* Cotizaciones, facturas y reportes que ya existen: se manda
                      el enlace, que siempre muestra lo que es verdad hoy. */}
                  <IconButton
                    icon={<Icon name="link" size={14} />}
                    ariaLabel={t('messages.attachDocument')}
                    size="sm"
                    variant="outline"
                    onClick={() => setEligiendoDoc(true)}
                  />
                  {detalleRespuesta?.hoursLeft != null && detalleRespuesta.hoursLeft > 0 && (
                    <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                      {t('messages.windowLeft', { hours: Math.max(Math.ceil(detalleRespuesta.hoursLeft), 1) })}
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  {/* Programar. Junto a enviar porque es la misma acción con
                      otra hora, no una función aparte escondida en un menú. */}
                  <IconButton
                    icon={<Icon name="clock" size={14} />}
                    ariaLabel={t('messages.schedule.title', 'Programar el envío')}
                    size="sm"
                    variant="outline"
                    disabled={!texto.trim() && adjuntos.length === 0}
                    onClick={() => setProgramando(true)}
                  />
                  {/* El botón aparece solo si hay alguna guardada: ofrecer una
                      lista vacía es un clic que no lleva a ningún lado. */}
                  {(respuestas?.replies.length ?? 0) > 0 && (
                    <Button variant="outline" tone="muted" size="sm" onClick={() => setEligiendo(true)}>
                      {t('messages.savedReplies')}
                    </Button>
                  )}
                  <Button variant="solid" tone="cyan" size="sm" loading={send.isPending}
                    disabled={!texto.trim() && adjuntos.length === 0} onClick={() => { void responder(); }}>
                    {t('messages.sendBtn')}
                  </Button>
                </div>
              </>
            )}
          </div>
      </div>

      <EnviarPlantilla
        open={enviandoPlantilla}
        onClose={() => setEnviandoPlantilla(false)}
        threadId={threadRespuesta.id}
        destinatario={group.name}
      />

      <ProgramarEnvio
        open={programando}
        onClose={() => setProgramando(false)}
        threadId={threadRespuesta.id}
        body={texto}
        attachmentIds={adjuntos.map(a => a.id)}
        replyToPlatformId={citando?.platformMessageId ?? null}
        hoursLeft={detalleRespuesta?.hoursLeft ?? null}
        onProgramado={() => { setTexto(''); setAdjuntos([]); setCitando(null); }}
      />

      {/* Elegir una respuesta guardada. Modal y no dropdown: hay que poder leer
          el texto entero antes de insertarlo — un menú de una línea por opción
          obliga a elegir a ciegas por el título. */}
      <Modal
        open={eligiendo}
        onClose={() => setEligiendo(false)}
        title={t('messages.savedReplies')}
        description={t('messages.savedRepliesHint')}
        size="md"
      >
        {/* Buscador. Villa Sión tiene 39 respuestas: a partir de unas quince,
            una lista sin buscar es más lenta que escribir el texto a mano, y la
            función deja de usarse.

            Busca también en el CUERPO, porque así es como uno se acuerda: no
            "¿cómo llamé a la de la ubicación?" sino "la que dice lo del portón
            de bambú". Un buscador que solo mira el título obliga a recordar la
            etiqueta que uno mismo puso hace tres meses. */}
        {(respuestas?.replies?.length ?? 0) > 8 && (
          <div style={{ marginBottom: 'var(--evo-space-3)' }}>
            <Input
              value={buscarRespuesta}
              onChange={setBuscarRespuesta}
              placeholder={t('messages.savedRepliesSearch')}
            />
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
          {respuestasFiltradas.length === 0 && buscarRespuesta.trim() && (
            <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
              {t('messages.savedRepliesNoMatch')}
            </span>
          )}
          {respuestasFiltradas.map(r => (
            <Pressable key={r.id} onPress={() => insertarRespuesta(r.body)} fullWidth ariaLabel={r.title}>
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)',
                padding: 'var(--evo-space-3)', borderRadius: 'var(--evo-r)',
                background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)',
                textAlign: 'left',
              }}>
                <span style={{ fontSize: 'var(--evo-fs-body)', fontWeight: 'var(--evo-fw-medium)', color: 'var(--evo-fg-1)' }}>
                  {r.title}
                </span>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {r.body}
                </span>
              </div>
            </Pressable>
          ))}
        </div>
      </Modal>

      {/* Elegir un documento ya hecho. Se inserta su ENLACE en el mensaje: el
          cliente entra y ve el estado de hoy, no una copia congelada del día en
          que se mandó. */}
      <Modal
        open={eligiendoDoc}
        onClose={() => setEligiendoDoc(false)}
        title={t('messages.attachDocument')}
        description={t('messages.attachDocumentHint')}
        size="md"
      >
        <ListState
          isLoading={documentos.isLoading}
          isError={documentos.isError}
          error={documentos.error}
          isEmpty={!documentos.isLoading && (documentos.data?.documentos.length ?? 0) === 0}
          emptyTitle={t('messages.noDocuments')}
          size="sm"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
            {(documentos.data?.documentos ?? []).map(d => (
              <Pressable key={`${d.tipo}-${d.id}`} onPress={() => insertarDocumento(d)} fullWidth ariaLabel={d.titulo}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)',
                  padding: 'var(--evo-space-3)', borderRadius: 'var(--evo-r)',
                  background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)',
                  textAlign: 'left',
                }}>
                  <span style={{ color: 'var(--evo-fg-3)', display: 'inline-flex', flexShrink: 0 }}>
                    <Icon name={d.tipo === 'report' ? 'reports' : d.tipo === 'invoice' ? 'invoice' : 'billing'} size={14} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.titulo}
                    </span>
                    <span style={{ fontSize: 'var(--evo-fs-tiny)', color: 'var(--evo-fg-4)' }}>
                      {t(`messages.documentType.${d.tipo}`)}
                      {d.fecha ? ` · ${new Date(d.fecha).toLocaleDateString('es', { day: 'numeric', month: 'short', year: '2-digit' })}` : ''}
                    </span>
                  </span>
                  {d.estado && (
                    <Pill size="sm" variant="subtle" tone="neutral">
                      {t(`billing.status.${d.estado}`, { defaultValue: d.estado })}
                    </Pill>
                  )}
                </div>
              </Pressable>
            ))}
          </div>
        </ListState>
      </Modal>
    </div>
  );
}

// ── Pantalla ─────────────────────────────────────────────────────────────────

export function MessagesPage() {
  const { t } = useTranslation();
  const { setOverride } = useTopBarCtx();
  const toast = useToast();
  const [platform, setPlatform] = useState('');
  const [estado, setEstado] = useState('');
  // Categoría aparte, no un valor más de `estado`: lo archivado no aparece en
  // ninguna otra vista. "Todos" significa todo lo ACTIVO — si archivar no
  // sacara nada de "Todos", no serviría para nada.
  const [verArchivados, setVerArchivados] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [seleccionado, setSeleccionado] = useState<string | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [fusionando, setFusionando] = useState(false);
  /** El panel del contacto queda como lo dejaste: quien trabaja con la ficha a
   *  la vista la quiere siempre, y quien no, nunca. */
  const [verFicha, setVerFicha] = useState(() => {
    try { return localStorage.getItem('evo-messages-ficha') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('evo-messages-ficha', verFicha ? '1' : '0'); } catch { /* sin storage, se pierde y no pasa nada */ }
  }, [verFicha]);
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, error } = useDmInbox(platform, estado, verArchivados);
  const takeover = useTakeOverThread();
  const release = useReleaseThread();
  const setRead = useSetThreadsRead();
  const setHandledBulk = useSetThreadsHandled();
  const setArchived = useSetThreadsArchived();

  useEffect(() => {
    setOverride({
      title: t('messages.title'),
      subtitle: t('messages.subtitle'),
      // REGLA #7: variante -large (viewBox 0 0 56 56).
      heroIcon: 'messages',
      heroIconColor: 'var(--evo-cyan)',
    });
    return () => setOverride({});
  }, [t, setOverride]);

  const grupos = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const all = agrupar(data?.threads ?? []);
    if (!q) return all;
    return all.filter(g =>
      g.name.toLowerCase().includes(q) || (g.lastPreview ?? '').toLowerCase().includes(q));
  }, [data, busqueda]);

  const grupoActivo = grupos.find(g => g.key === seleccionado) ?? null;

  function toggleMarcado(key: string, sel: boolean) {
    setMarcados(prev => { const next = new Set(prev); if (sel) next.add(key); else next.delete(key); return next; });
  }

  /** Ids de hilo de todos los contactos marcados. Un contacto puede tener
   *  varias conversaciones (Instagram y Facebook, por ejemplo) y la selección
   *  es por contacto: la acción tiene que alcanzarlas a todas. */
  function idsMarcados(): number[] {
    return grupos.filter(g => marcados.has(g.key)).flatMap(g => g.threads.map(th => th.id));
  }

  function bulkLeido(leido: boolean) {
    const ids = idsMarcados();
    if (ids.length === 0) return;
    setRead.mutate({ ids, read: leido }, {
      onSuccess: () => setMarcados(new Set()),
      onError: err => toast.error(err instanceof ApiError ? err.message : String(err)),
    });
  }

  function bulkArchivar(archivar: boolean) {
    const ids = idsMarcados();
    if (ids.length === 0) return;
    setArchived.mutate({ ids, archived: archivar }, {
      onSuccess: () => {
        setMarcados(new Set());
        // La conversación abierta pudo haberse ido de la vista.
        setSeleccionado(null);
        toast.info(archivar
          ? t('messages.archive.done', { count: ids.length })
          : t('messages.archive.undone', { count: ids.length }));
      },
      onError: err => toast.error(err instanceof ApiError ? err.message : String(err)),
    });
  }

  /** Dar por cerradas (o reabrir) todas las marcadas. */
  function bulkAtendida(atendida: boolean) {
    const ids = idsMarcados();
    if (ids.length === 0) return;
    setHandledBulk.mutate({ ids, handled: atendida }, {
      onSuccess: () => setMarcados(new Set()),
      onError: err => toast.error(err instanceof ApiError ? err.message : String(err)),
    });
  }

  /** Bulk tomar/devolver: aplica a todos los hilos de los contactos marcados. */
  function bulkTomada(tomar: boolean) {
    const m = tomar ? takeover : release;
    for (const g of grupos) {
      if (!marcados.has(g.key)) continue;
      for (const th of g.threads) {
        if (tomar ? th.takenOverAt == null : th.takenOverAt != null) {
          m.mutate(th.id, {
            onError: err => toast.error(err instanceof ApiError ? err.message : String(err)),
          });
        }
      }
    }
    setMarcados(new Set());
  }

  // ANTES del return: un hook debajo de un return condicional cambia la
  // cantidad de hooks entre renders y React tira la pantalla entera.
  const recorrido = useRecorrido('messaging', { hayDatos: grupos.length > 0, listo: !isLoading });

  return (
    <div className="flex flex-col h-full animate-fade-blur-in" style={{ background: 'var(--evo-bg)' }}>
      <Recorrido
        pasos={recorrido.pasos}
        abierto={recorrido.abierto}
        onCerrar={recorrido.cerrar}
        onTerminar={recorrido.terminar}
      />
      <FilterRow>
        <FilterRow.Search value={busqueda} onChange={setBusqueda} placeholder={t('messages.searchPlaceholder')} />
        {/* Solo los canales que de verdad tienen conversaciones: ofrecer
            WhatsApp a una cuenta que nunca lo conectó es un filtro que nunca
            devuelve nada. */}
        {(data?.platforms.length ?? 0) > 1 && (
          <div data-tour-id="messaging-canal" style={{ display: 'flex' }}>
          <FilterRow.Dropdown<string>
            value={platform}
            onChange={setPlatform}
            placeholder={t('messages.allChannels')}
            options={[
              { value: '', label: t('messages.allChannels') },
              ...(data?.platforms ?? []).map(p => ({ value: p, label: t(`messages.channel.${p}`, { defaultValue: p }) })),
            ]}
          />
          </div>
        )}
        <div data-tour-id="messaging-estado" style={{ display: 'flex' }}>
        <FilterRow.Dropdown<string>
          value={verArchivados ? 'archived' : estado}
          onChange={val => {
            // Elegir "Archivados" cambia de vista, no de filtro: los estados
            // no aplican ahí, y mezclarlos haría creer que "Todos" incluye lo
            // archivado.
            if (val === 'archived') { setVerArchivados(true); setEstado(''); return; }
            setVerArchivados(false);
            setEstado(val ?? '');
          }}
          placeholder={t('messages.allStates')}
          options={[
            { value: '', label: t('messages.allStates') },
            { value: 'unanswered', label: `${t('messages.unanswered')} (${data?.counts.unanswered ?? 0})` },
            { value: 'expiring', label: `${t('messages.expiring')} (${data?.counts.expiring ?? 0})` },
            { value: 'answered', label: `${t('messages.answered')} (${data?.counts.answered ?? 0})` },
            { value: 'archived', label: `${t('messages.archived')} (${data?.archivedCount ?? 0})` },
          ]}
        />
        </div>
      </FilterRow>

      {/* REGLA #7: SAB a page root, post-FilterRow, full-width. */}
      <SelectionActionBar
        visible={marcados.size > 0}
        countLabel={t('messages.selected', { count: marcados.size })}
        onClearSelection={() => setMarcados(new Set())}
      >
        {/* En la vista de archivados la única acción que tiene sentido es
            sacarlas de ahí: marcar leído o volver a archivar algo archivado no
            significa nada. */}
        {verArchivados ? (
          <SelectionActionBar.Action
            label={t('messages.unarchive')}
            loading={setArchived.isPending}
            onClick={() => bulkArchivar(false)}
          />
        ) : (
          <>
            <SelectionActionBar.Action
              label={t('messages.markRead')}
              loading={setRead.isPending}
              onClick={() => bulkLeido(true)}
            />
            <SelectionActionBar.Action
              label={t('messages.markUnread')}
              loading={setRead.isPending}
              onClick={() => bulkLeido(false)}
            />
            <SelectionActionBar.Action
              label={t('messages.archive.action')}
              loading={setArchived.isPending}
              onClick={() => bulkArchivar(true)}
              confirmRequired
            />
            {/* La misma persona entra dos veces —WhatsApp con su número,
                formulario con su correo— y queda con dos fichas y la
                conversación partida. Se juntan desde donde se ve el problema. */}
            {/* Lo mismo que el botón de la conversación, para varias de una:
                es la acción que uno repite cuando vuelve de mirar la bandeja. */}
            <SelectionActionBar.Action
              label={t('messages.markHandled')}
              loading={setHandledBulk.isPending}
              onClick={() => bulkAtendida(true)}
            />
            <SelectionActionBar.Action
              label={t('messages.markPending')}
              loading={setHandledBulk.isPending}
              onClick={() => bulkAtendida(false)}
            />
            {marcados.size > 1 && (
              <SelectionActionBar.Action
                label={t('messages.mergeAction')}
                onClick={() => setFusionando(true)}
              />
            )}
            <SelectionActionBar.Action
              label={t('messages.takeControl')}
              loading={takeover.isPending}
              onClick={() => bulkTomada(true)}
            />
            <SelectionActionBar.Action
              label={t('messages.releaseControl')}
              loading={release.isPending}
              onClick={() => bulkTomada(false)}
            />
          </>
        )}
      </SelectionActionBar>

      <FusionarContactos
        open={fusionando}
        onClose={() => setFusionando(false)}
        conversaciones={grupos.filter(g => marcados.has(g.key)).map(g => ({
          contactId: g.contactId,
          nombre: g.name,
          channelIds: g.threads.map(th => th.channelId),
        }))}
        onHecho={() => setMarcados(new Set())}
      />

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Lista agrupada por contacto */}
        <div data-tour-id="messaging-lista" style={{ width: 'clamp(300px, 33%, 480px)', flexShrink: 0, borderRight: '1px solid var(--evo-stroke)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ListToolbar
            allSelected={grupos.length > 0 && grupos.every(g => marcados.has(g.key))}
            someSelected={marcados.size > 0 && !grupos.every(g => marcados.has(g.key))}
            onSelectAll={sel => setMarcados(sel ? new Set(grupos.map(g => g.key)) : new Set())}
            onExpandAll={() => setCollapsedBands(new Set())}
            onCollapseAll={() => setCollapsedBands(new Set(ALL_BANDS))}
            expandLabel={t('common.expandAll')}
            collapseLabel={t('common.collapseAll')}
          />
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <ListState
              isLoading={isLoading}
              isError={isError}
              error={error}
              isEmpty={grupos.length === 0}
              emptyTitle={t('messages.emptyList')}
              emptyDescription={t('messages.emptyListHint')}
            >
              <TimeBandedList<ConvGroup, string>
                items={grupos}
                getId={g => g.key}
                getTimestamp={g => g.lastAt ?? new Date(0).toISOString()}
                collapsedBands={collapsedBands}
                onToggleBand={band => setCollapsedBands(prev => {
                  const next = new Set(prev);
                  if (next.has(band)) next.delete(band); else next.add(band);
                  return next;
                })}
                checkedIds={marcados}
                onBandCheck={(ids, allChecked) => { ids.forEach(id => toggleMarcado(id, !allChecked)); }}
                renderItem={g => (
                  <ListItem
                    key={g.key}
                    itemId={g.key}
                    tourId={g.key === grupos[0]?.key ? 'messaging-fila' : undefined}
                    active={seleccionado === g.key}
                    onClick={() => setSeleccionado(g.key)}
                    selected={marcados.has(g.key)}
                    onSelect={(sel: boolean) => toggleMarcado(g.key, sel)}
                    // Barra cian mientras NO la abriste.
                    //
                    // Antes iba sobre "sin responder", y eso dura hasta que
                    // contestás: abrías una conversación, pasabas a otra, y la
                    // primera seguía marcada como si no la hubieras visto.
                    // "Sin responder" y "sin leer" son dos cosas distintas —
                    // la primera la sigue contando el indicador del menú, la
                    // segunda es la que se apaga al abrir.
                    unread={g.threads.some(th => !th.isRead)}
                    title={g.name}
                    subtitle={esSinTexto(g.lastPreview) ? t('messages.nonTextContent') : g.lastPreview!}
                    meta={
                      <span style={{ display: 'inline-flex', gap: 4 }}>
                        {g.platforms.map(p => <CanalIcono key={p} platform={p} />)}
                      </span>
                    }
                    // Quién lleva cada conversación se ve desde la lista: si hay
                    // que buscarlo abriendo una por una, nadie lo mira.
                    badges={[<EstadoPills key="estado" g={g} />]}
                  />
                )}
              />
            </ListState>
          </div>
        </div>

        {/* Bitácora del contacto */}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {grupoActivo == null ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>
                {t('messages.pickOne')}
              </span>
            </div>
          ) : (
            <Bitacora
              key={grupoActivo.key}
              group={grupoActivo}
              verFicha={verFicha}
              onVerFicha={() => setVerFicha(v => !v)}
            />
          )}
        </div>

      {/* La ficha del contacto, al costado. Es el MISMO componente del módulo
          Contactos —no una copia con la mitad de los campos— así que lo que se
          edita acá se edita como allá. */}
      {verFicha && grupoActivo?.contactId != null && (
        <div style={{
          width: 'var(--evo-contact-panel-w)', flexShrink: 0, minHeight: 0,
          borderLeft: '1px solid var(--evo-stroke)', overflowY: 'auto',
        }}>
          <ContactDetail
            id={grupoActivo.contactId}
            onClose={() => setVerFicha(false)}
            onDeleted={() => setVerFicha(false)}
          />
        </div>
      )}

      </div>
    </div>
  );
}
